import { computed, reactive, ref, watch } from '@nuxtjs/composition-api';
import { useApolloClient, useResult } from '@vue/apollo-composable/dist';
import useCookie, { CookieKeys } from '../useCookie';
import { CustomJSON, toDate } from '../useHelper';
import { useDayjs } from '../useUtils';
import {
  EventSort,
  SearchEventInput,
  useCorporationTypeNamesQuery,
  useCorporationTypesQuery,
  useGroupIndustriesQuery,
  useIndustryNamesQuery,
} from '~/graphql/graphqlOperations';
import useEnums, { EnumKeys } from '~/compositions/useEnums';

const conditionDefault = {
  useClosingDate: false,
  from: null,
  to: null,
  industryCodes: [],
  prefectures: [],
  corporationTypes: [],
  keyword: '',
  corporationId: null,
  sort: EventSort.EventLatest,
} as SearchEventInput;

interface UseInternshipSearchOptions {
  temp: boolean;
}

const defaultOptions: UseInternshipSearchOptions = {
  temp: false,
};

/**
 * 求人票検索のビジネスロジック
 */
export default function useInternshipsSearch(
  _options?: UseInternshipSearchOptions
) {
  /**
   * 検索条件の保存、表示ロジック
   * 検索項目はcookie内のInternshipSettings、InternshipSettingsTemporaryに保存される。
   * InternshipSettings：検索ボタンクリックしたときの検索条件保存用。
   * InternshipSettingsTemporary：検索条件の一時保存用。
   * URLが'/jobs_search'を含まない時(検索画面ではない)、InternshipSettingsTemporaryは削除。(pages/jobs_search/internship)
   * InternshipSettingsTemporaryが必要な理由は、検索項目を選んだが、検索しなかった場合(戻るボタンなどで画面を離れた場合)、検索枠に表示されないという状態を作るため。
   *
   */

  // Object.assign：第一引数に第二引数をコピーするメソッド
  const options = Object.assign(defaultOptions, _options);
  const { getCookie, setCookie } = useCookie();
  const client = useApolloClient('default').resolveClient();

  // ラベル用
  const { getLabel: getPrefecgureLabel } = useEnums(EnumKeys.Prefecture);
  let readKey = CookieKeys.InternshipSettings;
  if (options.temp) {
    // クッキー(InternshipSettingsTemporary)に一時保存
    const tempValue = getCookie(CookieKeys.InternshipSettingsTemporary);
    if (tempValue) {
      // readKeyの値をInternshipSettingsTemporaryに変更
      readKey = CookieKeys.InternshipSettingsTemporary;
    }
  }
  // 検索条件を取得
  const conditions = reactive<SearchEventInput>(
    Object.assign(
      conditionDefault,
      JSON.parse(
        getCookie(readKey, { doNotParse: true }) ?? '{}',
        CustomJSON.reviver
      )
    )
  );

  // 検索条件を保存 or 一時保存
  // 検索実行時はfalseが渡ってくるのでInternshipSettingsが適用される
  const saveConditions = (temp: boolean = true) => {
    // @ts-ignore
    // console.warn(client.cache.data.data);
    // TODO: この処理は、conditionsの中にある from と to の日付を意味するデータの
    //       watch関数での変更検知が現状出来ないため、仕方なしに、条件保存のタイミングで、
    //       無理やりキャッシュを消す事によって検索結果の妥当性を維持するためのもの。
    //       検索条件が変わったのでクエリ結果として保存されている【企業実習一覧】データを
    //       Apolloのクエリ結果キャッシュから消す
    client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'internships' });
    const whiteKey = temp
      ? CookieKeys.InternshipSettingsTemporary
      : CookieKeys.InternshipSettings;
    setCookie(whiteKey, JSON.stringify(conditions, CustomJSON.replacer));
  };

  /**
   * 開催地
   */
  // 都道府県ラベル
  const prefecturesLabel = computed(() =>
    (conditions.prefectures || [])
      .map((item) => getPrefecgureLabel(item))
      .join('、')
  );

  // 都道府県クリア
  const clearPrefectures = () => {
    conditions.prefectures?.splice(0);
    saveConditions();
  };

  // 選択済み都道府県
  const selectedPrefectures = ref(conditions.prefectures || []);

  /**
   * 業種
   */
  // グループ分けした業種を取得
  const groupIndustriesQueryVariables = reactive({
    schoolId: 0,
  });
  const groupIndustriesQueryOptions = reactive({ enabled: false });
  const { result: groupIndustriesQueryResult } = useGroupIndustriesQuery(
    groupIndustriesQueryVariables,
    groupIndustriesQueryOptions
  );
  const groupIndustries = useResult(groupIndustriesQueryResult, []);
  const getGroupIndustries = (schoolId?: number) => {
    groupIndustriesQueryVariables.schoolId = schoolId || 0;
    groupIndustriesQueryOptions.enabled = true;
    return groupIndustries;
  };

  // 業種名を取得
  const industryNamesQueryVariables = reactive({
    industryCodes: conditions.industryCodes || [],
  });
  const industryNamesQueryOptions = reactive({
    enabled: false,
  });
  const { result: industryNamesQueryResult } = useIndustryNamesQuery(
    industryNamesQueryVariables,
    industryNamesQueryOptions
  );
  const industryNames = useResult(industryNamesQueryResult, '');
  const industriesLabel = computed(() => {
    if (conditions.industryCodes?.length) {
      industryNamesQueryOptions.enabled = true;
      return industryNames.value;
    } else {
      return '';
    }
  });

  // 業種クリア
  const clearIndustries = () => {
    conditions.industryCodes?.splice(0);
    saveConditions();
  };

  // 選択済み業種
  const selectedIndustryCodes = ref(conditions.industryCodes || []);

  /**
   * 企業タイプ
   */
  // 企業タイプ一覧取得
  const corporationTypesQueryVariables = reactive({
    schoolId: 0,
  });
  const corporationTypesQueryOptions = reactive({
    enabled: false,
  });
  const { result: corporationTypesQueryResult } = useCorporationTypesQuery(
    corporationTypesQueryVariables,
    corporationTypesQueryOptions
  );
  const corporationTypes = useResult(corporationTypesQueryResult, []);
  const getCorporationTypes = (schoolId?: number) => {
    corporationTypesQueryVariables.schoolId = schoolId || 0;
    corporationTypesQueryOptions.enabled = true;
    return corporationTypes;
  };

  // 企業タイプ名を取得
  const corporationTypeNamesQueryVariables = reactive({
    corporationTypeCodes: conditions.corporationTypes || [],
  });
  const corporationTypeNamesQueryOptions = reactive({
    enabled: false,
  });
  const { result: corporationTypeNamesQueryResult } =
    useCorporationTypeNamesQuery(
      corporationTypeNamesQueryVariables,
      corporationTypeNamesQueryOptions
    );
  const corporationTypeNames = useResult(corporationTypeNamesQueryResult, '');
  const corporationTypesLabel = computed(() => {
    if (conditions.corporationTypes?.length) {
      corporationTypeNamesQueryVariables.corporationTypeCodes =
        conditions.corporationTypes || [];
      corporationTypeNamesQueryOptions.enabled = true;
      return corporationTypeNames.value;
    } else {
      return '';
    }
  });

  // 企業タイプクリア
  const clearCorporationTypes = () => {
    conditions.corporationTypes?.splice(0);
    saveConditions();
  };
  // 選択済み企業タイプ
  const selectedCorporationTypes = ref(conditions.corporationTypes || []);

  const today = new Date();
  /**
   * 日付From
   */
  const inputedDateFrom = ref(toDate(conditions.from) || today);

  /**
   * 日付To
   */
  const inputedDateTo = ref(
    toDate(conditions.to) ||
      new Date(today.getFullYear(), today.getMonth() + 2, today.getDate())
  );

  /**
   * フリーワード
   */
  const inputedFreeWord = ref(conditions.keyword || '');
  const dayjs = useDayjs();
  /**
   * 選択されたラベルを表示
   */
  const selectedLabels = computed(() => {
    // 開催期間
    const searchTerm = ref('');
    // 開催期間を連結(2020/12/10 ~ 2021/10/05)のような感じ
    searchTerm.value =
      // @ts-ignore
      dayjs(inputedDateFrom.value).format('YYYY/MM/DD') +
      ' ' +
      '〜' +
      ' ' +
      // @ts-ignore
      dayjs(inputedDateTo.value).format('YYYY/MM/DD');
    // 検索項目を配列化
    // フリーワード→業種→開催地→期間→企業のタイプの順番
    const labels = [
      inputedFreeWord.value,
      industriesLabel.value,
      prefecturesLabel.value,
      searchTerm.value,
      corporationTypesLabel.value,
    ];
    // 検索枠に表示するために整える
    const prepareLabels = labels
      .filter((label) => !!label)
      .join(' ')
      .replace(/、/g, ' ');
    return prepareLabels;
  });
  return {
    conditions,
    saveConditions,
    // 都道府県
    selectedPrefectures,
    prefecturesLabel,
    clearPrefectures,
    // 業種
    getGroupIndustries,
    industriesLabel,
    clearIndustries,
    selectedIndustryCodes,
    // 企業タイプ
    getCorporationTypes,
    corporationTypesLabel,
    clearCorporationTypes,
    selectedCorporationTypes,
    // 検索日付
    inputedDateFrom,
    inputedDateTo,
    // フリーワード
    inputedFreeWord,
    // 選択されたラベルの表示
    selectedLabels,
  };
}
